import {
  DownloadOutlined,
  FilterOutlined,
  PlusOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import {
  deletePartner,
  downloadCSV,
  getListPartner,
} from 'api/partner/partner.api';
import {
  ButtonBreadcrumb,
  ButtonTextIcon,
  LineVertical,
} from 'components/Button';
import Breadcrumb from 'components/Layout/ApplicationLayout/Breadcrumb';
import HeaderListDataLayout from 'components/Layout/HeaderListDataLayout';
import LayoutDefault from 'components/Layout/LayoutDefault';
import Pagination from 'components/Pagination';
import {
  defaultPageSizeOptions,
  defaultSelectPageSizeOption,
} from 'constant/Pagination/pageSizeOption';
import { SORT_DIRECTION } from 'constant/sortConstants';
import useErrorNetworkMessage from 'hook/useErrorNetworkMessage';
import useCustomMutate from 'hook/useQuery/useCustomMutate';
import useCustomQuery from 'hook/useQuery/useCustomQuery';
import useSearchKeyword from 'hook/useSearchKeyword';
import useSetTitlePage from 'hook/useSetTitlePage';
import TableData from 'page/Application/ManageCompanyList/CompanyList/component/TableData';
import useMultiLanguageCompanyList from 'page/Application/ManageCompanyList/CompanyList/hook/useMultiLanguageCompanyList';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useRecoilState, useRecoilValue } from 'recoil';
import { idProduct, listProduct, product } from 'recoil/atom/selectProduct';
import SelectAllTable from 'page/Application/ManageAccountSoliton/AccountSolitonList/component/SelectAllTable';
import { IconWrapper } from 'page/Application/ManagerAccountPartner/AccountPartnerList/styled';
import ModalFilter from 'page/Application/ManageCompanyList/CompanyList/component/ModalFilter';
import { userInformation } from 'recoil/atom/authentication';
import useMultiLanguageRegisterAccount from 'page/Application/ManageAccountSoliton/RegisterAccountSoliton/hook/useMultiLanguageRegisterAccount';
import dayjs from 'dayjs';
import { DATE_FORMAT_LIST } from 'constant/notificationConstants';

const initFilterData = {
  partnerName: '',
  domainName: '',
  isAutoApprove: '',
  productId: '',
  partnerType: '',
  solitonEmail: '',
};

const CompanyList = () => {
  useSetTitlePage('企業管理 | Soliton パートナーサイト', true);

  const { role } = useRecoilValue(userInformation);
  const isSolitonUser = role === 'ROLE_SOLITON_USER';
  const {
    COMPANY_LIST_TITLE,
    ACCOUNT_LIST_DELETE_SUCCESS,
    COMPANY_LIST_PLACEHOLDER_SEARCH,
  } = useMultiLanguageCompanyList();
  const { REGISTER_ACCOUNT_REGISTER_EXPORT_FILE_MESSAGE_SUCCESS } =
    useMultiLanguageRegisterAccount();
  const [itemDelete, setItemDelete] = useState('');
  const [sortedInfo, setSortedInfo] = useState({});
  const [page, setPage] = useState(10);
  const [current, setCurrent] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [, setListSelectedProduct] = useRecoilState(listProduct);
  const [, setProductSelected] = useRecoilState(product);
  const [, setIdProductSelected] = useRecoilState(idProduct);
  const [checkProductIdExist, setProductIdExist] = useState(true);
  const [selectAllData, setSelectAllData] = useState(false);
  const [, setSelectAllPage] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [listIdSelected, setListIdSelected] = useState([]);
  const [filterData, setFilterData] = useState(initFilterData);
  const [resetStatusFilter, setResetStatusFilter] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const location = useLocation();
  const { searchKeywordState, filterDataState } = location.state || {};
  const { COMPANY_LIST_PLACEHOLDER_SEARCH_NODATA, COMPANY_LIST_NO_DATA } =
    useMultiLanguageCompanyList();

  const navigate = useNavigate();
  const { useQueryAPI } = useCustomQuery();
  const { useMutationAPI } = useCustomMutate();
  const {
    searchKeyword,
    setSearchKeyword,
    emptyText,
    setEmptyText,
    setIsFilter,
  } = useSearchKeyword();
  const { isOnline, errorNetWork, contextErrorNetWork } =
    useErrorNetworkMessage();

  const listItemSA = useMemo(() => {
    return [
      {
        title: '企業管理',
      },
    ];
  }, []);

  const listItemSU = useMemo(() => {
    return [
      {
        title: <Link to={'/my-page'}>マイページ</Link>,
      },
      {
        title: '企業管理',
      },
    ];
  }, []);

  const listItemByRole = useMemo(() => {
    return {
      ROLE_SOLITON_ADMIN: listItemSA,
      ROLE_SOLITON_USER: listItemSU,
    };
  }, [listItemSA, listItemSU]);

  const { data: companyList = [], isLoading } = useQueryAPI({
    api: getListPartner,
    key: 'companyList',
    params: {
      pageNumber: current,
      pageSize: page,
      keyword: encodeURIComponent(searchKeyword),
      sortField: sortedInfo?.sortField ?? '',
      sortDirection: sortedInfo?.sortDirection ?? '',
      domainName: filterData?.domainName ?? '',
      isAutoApprove: filterData?.isAutoApprove ?? '',
      partnerName: filterData?.partnerName ?? '',
      partnerType: filterData?.partnerType ?? '',
      productId: checkProductIdExist ? (filterData?.productId ?? '') : '',
      solitonEmail: filterData?.solitonEmail ?? '',
    },
  });

  const { mutate: mutateDownloadCSV, isLoading: loadingDownloadCSV } =
    useMutationAPI({
      api: downloadCSV,
      refetchKey: 'downloadCSV',
      messageSuccess: REGISTER_ACCOUNT_REGISTER_EXPORT_FILE_MESSAGE_SUCCESS,
      downloadFile: true,
      fileName: `${dayjs.utc().tz('Asia/Tokyo')?.format(DATE_FORMAT_LIST[1])}_企業出力`,
    });

  const { mutate: deleteSinglePartner } = useMutationAPI({
    api: deletePartner,
    refetchKey: 'companyList',
    messageSuccess: `${itemDelete} ${ACCOUNT_LIST_DELETE_SUCCESS}`,
  });

  const onSearch = (value) => {
    if (isOnline) {
      const trimmedValue = value?.trim();
      setSearchKeyword(trimmedValue);
      if (value.length > 0) {
        setEmptyText(COMPANY_LIST_PLACEHOLDER_SEARCH_NODATA);
      } else {
        setEmptyText(COMPANY_LIST_NO_DATA);
      }
      setFilterData(initFilterData);
      setResetStatusFilter(true);
      handleSelectRowToDefault();
    } else {
      errorNetWork();
    }
  };

  const handleSelectAllRecord = () => {
    if (isOnline) {
      if (!selectAllData) {
        setSelectAllData((current) => !current);
      } else {
        handleSelectRowToDefault();
      }
    } else {
      errorNetWork();
    }
  };

  const handleSelectRowToDefault = () => {
    if (isOnline) {
      setSelectAllData(false);
      setSelectAllPage(false);
      setSelectedRowKeys([]);
      setListIdSelected([]);
    } else {
      errorNetWork();
    }
  };

  const handleResetData = () => {
    if (isOnline) {
      setPage(10);
      setCurrent(1);
      setSortedInfo({});
      setFilterData(initFilterData);
      setResetStatusFilter(true);
      setSearchValue('');
      setSearchKeyword('');
      setEmptyText(COMPANY_LIST_NO_DATA);
    } else {
      errorNetWork();
    }
  };

  const handlePagingChange = (pageNumber, pageSize) => {
    if (isOnline) {
      setCurrent(pageNumber);
      setPage(pageSize);
    } else {
      errorNetWork();
    }
  };

  const handleChangePageSize = (value) => {
    if (isOnline) {
      setPage(value);
      setCurrent(1);
    } else {
      errorNetWork();
    }
  };

  const handleChange = (pagination, filters, sorter) => {
    if (isOnline) {
      setSortedInfo({
        sortField: sorter.order && sorter.field,
        sortDirection: sorter.order && SORT_DIRECTION[sorter.order],
        order: sorter.order,
        columnKey: sorter.columnKey,
      });
    } else {
      errorNetWork();
    }
  };

  const handleExportCSV = () => {
    if (isOnline) {
      const payload = {
        isCondition: selectAllData,
        partnerIds: listIdSelected,
        keyword: searchKeyword,
        partnerName: filterData?.partnerName ?? '',
        domainName: filterData?.domainName ?? '',
        isAutoApprove: filterData?.isAutoApprove ?? '',
        partnerType: filterData?.partnerType ?? '',
        solitonEmail: filterData?.solitonEmail ?? '',
        productId: filterData?.productId ?? '',
      };
      mutateDownloadCSV(payload);
    } else {
      errorNetWork();
    }
  };

  const handleOpenModal = () => {
    if (isOnline) {
      setIsModalOpen(true);
    } else {
      errorNetWork();
    }
  };

  const handleDeleteCompany = (id) => {
    if (isOnline) {
      deleteSinglePartner(id);
    } else {
      errorNetWork();
    }
  };

  const handleNavigate = () => {
    if (isOnline) {
      navigate('/manage-company-list/register');
      setProductSelected([]);
      setIdProductSelected([]);
    } else {
      errorNetWork();
    }
  };

  useEffect(() => {
    if (isOnline) {
      if (searchKeyword) {
        setCurrent(1);
      }
      setListSelectedProduct({});
    } else {
      errorNetWork();
    }
  }, [searchKeyword, setListSelectedProduct]);

  useEffect(() => {
    setSearchKeyword(searchKeywordState ?? '');
    setSearchValue(searchKeywordState ?? '');
    setFilterData(filterDataState ?? initFilterData);
    setEmptyText(COMPANY_LIST_NO_DATA);
    const updatedState = {
      searchKeywordState: '',
      filterDataState: '',
    };
    navigate(location.pathname, { state: updatedState, replace: true });
  }, []);

  return (
    <>
      {contextErrorNetWork}
      <Breadcrumb listItem={listItemByRole[role]} titlePage="企業管理">
        {!isSolitonUser && (
          <ButtonBreadcrumb
            text={'新規登録'}
            icon={<PlusOutlined />}
            onClick={handleNavigate}
          />
        )}
      </Breadcrumb>
      <ModalFilter
        width={'fit-content'}
        modalKey={'modalFilterCompany'}
        isModalOpen={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        filterDataState={filterDataState}
        setFilterData={setFilterData}
        setResetStatusFilter={setResetStatusFilter}
        resetStatusFilter={resetStatusFilter}
        setSortedInfo={setSortedInfo}
        setIsFilter={setIsFilter}
        isOnline={isOnline}
        errorNetWork={errorNetWork}
        setSearchKeywordList={setSearchKeyword}
        setSearchValueList={setSearchValue}
        setEmptyTextList={setEmptyText}
        setProductIdExist={setProductIdExist}
      />
      <LayoutDefault>
        <HeaderListDataLayout
          onSearch={onSearch}
          loading={isLoading}
          value={page}
          handleChangePageSize={handleChangePageSize}
          pageSizeOptions={defaultSelectPageSizeOption}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
          titleScreen={COMPANY_LIST_TITLE}
          searchPlaceholder={COMPANY_LIST_PLACEHOLDER_SEARCH}
        >
          <>
            <ButtonTextIcon
              text={'絞り込み検索'}
              icon={
                <IconWrapper>
                  <FilterOutlined style={{ color: '#9d1c49' }} />
                </IconWrapper>
              }
              onClick={handleOpenModal}
            />
            <LineVertical />
            <ButtonTextIcon
              text={'リフレッシュ'}
              icon={<RedoOutlined />}
              onClick={handleResetData}
            />
            <LineVertical />
            <ButtonTextIcon
              disabled={loadingDownloadCSV}
              text={'出力'}
              size={'middle'}
              icon={
                <IconWrapper
                  style={{
                    opacity: loadingDownloadCSV ? 0.3 : 1,
                  }}
                >
                  <DownloadOutlined style={{ color: '#9d1c49' }} />
                </IconWrapper>
              }
              onClick={handleExportCSV}
            />
            <LineVertical />
          </>
        </HeaderListDataLayout>
        {listIdSelected?.length !== 0 && (
          <SelectAllTable
            dataRaw={companyList.data}
            handleSelectAllRecord={handleSelectAllRecord}
            selectAllData={selectAllData}
            listIdSelected={listIdSelected}
          />
        )}
        <TableData
          dataRaw={companyList.data}
          isLoading={isLoading}
          handleDeleteCompany={handleDeleteCompany}
          setItemDelete={setItemDelete}
          emptyText={emptyText}
          onChange={handleChange}
          sortedInfo={sortedInfo}
          selectAllData={selectAllData}
          setSelectAllPage={setSelectAllPage}
          selectedRowKeys={selectedRowKeys}
          setSelectedRowKeys={setSelectedRowKeys}
          setSelectAllData={setSelectAllData}
          listIdSelected={listIdSelected}
          setListIdSelected={setListIdSelected}
          isSolitonUser={isSolitonUser}
          searchKeyword={searchKeyword}
          filterData={filterData}
        />

        <Pagination
          currentPage={current}
          handlePagingChange={handlePagingChange}
          total={companyList.data?.total}
          pageSize={page}
          pageSizeOptions={defaultPageSizeOptions}
          handleChangePageSize={handleChangePageSize}
        />
      </LayoutDefault>
    </>
  );
};
export default CompanyList;
